import { FC, useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { getStaffProfiles, updateStaffProfile, createStaffProfile, getOutlets, getOutletName } from '../../lib/scheduling';
import type { StaffWithProfile, Outlet } from '../../types/scheduling';
import { showToast } from '../../hooks/useToast';
import { supabase } from '../../lib/supabase';
import { useRole } from '../../hooks/useRole';

const StaffProfilesPage: FC = () => {
  const { isAdmin, loading: roleLoading } = useRole();
  const [staff, setStaff] = useState<StaffWithProfile[]>([]);
  const [outlets, setOutlets] = useState<Outlet[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [users, setUsers] = useState<any[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [staffData, outletsData, usersData] = await Promise.all([
        getStaffProfiles(),
        getOutlets(),
        loadAllUsers()
      ]);
      
      setStaff(staffData);
      setOutlets(outletsData);
      setUsers(usersData);
    } catch (error) {
      showToast('Failed to load staff data', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllUsers = async () => {
    // Get all staff users from user_roles
    const { data: staffRoles } = await supabase
      .from('user_roles')
      .select('user_id')
      .eq('role', 'staff');
    
    if (!staffRoles || staffRoles.length === 0) return [];
    
    const staffUserIds = staffRoles.map(r => r.user_id);
    
    // Get user profiles for those staff
    const { data } = await supabase
      .from('user_profiles')
      .select('user_id, display_name')
      .in('user_id', staffUserIds);
    
    return data || [];
  };

  const handleUpdate = async (userId: string, updates: any) => {
    try {
      await updateStaffProfile(userId, updates);
      showToast('Staff profile updated successfully', 'success');
      await loadData();
      setEditingUserId(null);
    } catch (error) {
      showToast('Failed to update staff profile', 'error');
      console.error(error);
    }
  };

  const handleInitializeProfile = async (userId: string) => {
    try {
      await createStaffProfile({
        user_id: userId,
        home_outlet_id: null,
        secondary_outlet_id: null,
        is_fixed: false,
        can_rotate: true,
        role: 'staff',
        is_active: true,
        last_assigned_outlet_id: null,
        last_assigned_date: null
      });
      showToast('Staff profile initialized successfully', 'success');
      await loadData();
    } catch (error) {
      showToast('Failed to initialize staff profile', 'error');
      console.error(error);
    }
  };

  if (roleLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  const staffUserIds = new Set(staff.map(s => s.user_id));
  const uninitializedUsers = users.filter(u => !staffUserIds.has(u.user_id));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Staff Profiles</h1>
        <p className="text-gray-600">Manage staff settings, skills, and branch assignments</p>
      </div>

      {/* Uninitialized Users */}
      {uninitializedUsers.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
          <h3 className="text-base sm:text-lg font-semibold text-yellow-900 mb-3">
            Users Without Staff Profiles ({uninitializedUsers.length})
          </h3>
          <div className="space-y-2">
            {uninitializedUsers.map((user) => (
              <div key={user.user_id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-white p-3 rounded gap-2">
                <span className="font-medium">{user.display_name}</span>
                <button
                  onClick={() => handleInitializeProfile(user.user_id)}
                  className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition text-sm"
                >
                  Initialize Profile
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Staff Profiles Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {/* Desktop Table View */}
        <div className="hidden lg:block">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Staff Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Home Outlet
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Secondary Outlet
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Leaderboard Points
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Can Rotate
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {staff.map((staffMember) => (
              <tr key={staffMember.user_id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {staffMember.display_name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {staffMember.profile.role}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingUserId === staffMember.user_id ? (
                    <select
                      defaultValue={staffMember.profile.home_outlet_id || ''}
                      onChange={(e) => handleUpdate(staffMember.user_id, { 
                        home_outlet_id: e.target.value ? parseInt(e.target.value) : null 
                      })}
                      className="px-2 py-1 border border-gray-300 rounded text-sm"
                    >
                      <option value="">No Home Outlet</option>
                      {outlets.map((outlet) => (
                        <option key={outlet.id} value={outlet.id}>
                          {getOutletName(outlet)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="text-sm text-gray-900">
                      {staffMember.home_outlet ? getOutletName(staffMember.home_outlet) : '-'}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {editingUserId === staffMember.user_id ? (
                    <select
                      defaultValue={staffMember.profile.secondary_outlet_id || ''}
                      onChange={(e) => handleUpdate(staffMember.user_id, { 
                        secondary_outlet_id: e.target.value ? parseInt(e.target.value) : null 
                      })}
                      className="px-2 py-1 border border-gray-300 rounded text-sm"
                    >
                      <option value="">No Secondary Outlet</option>
                      {outlets.map((outlet) => (
                        <option key={outlet.id} value={outlet.id}>
                          {getOutletName(outlet)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="text-sm text-gray-500">
                      {staffMember.secondary_outlet ? getOutletName(staffMember.secondary_outlet) : '-'}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm">
                    <span className="font-semibold">{staffMember.skill_score}</span> points
                    {staffMember.skill_score >= 100 && (
                      <span className="ml-2 text-xs text-purple-600">⭐ High Performer</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">From leaderboard</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleUpdate(staffMember.user_id, { 
                      is_fixed: !staffMember.profile.is_fixed 
                    })}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition ${
                      staffMember.profile.is_fixed
                        ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                  >
                    {staffMember.profile.is_fixed ? 'Fixed' : 'Rotating'}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleUpdate(staffMember.user_id, { 
                      can_rotate: !staffMember.profile.can_rotate 
                    })}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition ${
                      staffMember.profile.can_rotate
                        ? 'bg-green-100 text-green-800 hover:bg-green-200'
                        : 'bg-red-100 text-red-800 hover:bg-red-200'
                    }`}
                  >
                    {staffMember.profile.can_rotate ? 'Yes' : 'No'}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleUpdate(staffMember.user_id, { 
                      is_active: !staffMember.profile.is_active 
                    })}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition ${
                      staffMember.profile.is_active
                        ? 'bg-green-100 text-green-800 hover:bg-green-200'
                        : 'bg-red-100 text-red-800 hover:bg-red-200'
                    }`}
                  >
                    {staffMember.profile.is_active ? '✓ Active' : '✕ Inactive'}
                  </button>
                  {!staffMember.profile.is_active && (
                    <div className="text-xs text-gray-500 mt-1">Emergency leave/Absent</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {editingUserId === staffMember.user_id ? (
                    <button
                      onClick={() => setEditingUserId(null)}
                      className="text-gray-600 hover:text-gray-800"
                    >
                      Done
                    </button>
                  ) : (
                    <button
                      onClick={() => setEditingUserId(staffMember.user_id)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden divide-y divide-gray-200">
          {staff.map((staffMember) => (
            <div key={staffMember.user_id} className="p-4 space-y-3">
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-base font-medium text-gray-900">
                    {staffMember.display_name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {staffMember.profile.role}
                  </div>
                </div>
                {editingUserId === staffMember.user_id ? (
                  <button
                    onClick={() => setEditingUserId(null)}
                    className="text-gray-600 hover:text-gray-800 text-sm px-3 py-1 border border-gray-300 rounded"
                  >
                    Done
                  </button>
                ) : (
                  <button
                    onClick={() => setEditingUserId(staffMember.user_id)}
                    className="text-blue-600 hover:text-blue-800 text-sm px-3 py-1 border border-blue-300 rounded"
                  >
                    Edit
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Home Outlet</div>
                  {editingUserId === staffMember.user_id ? (
                    <select
                      defaultValue={staffMember.profile.home_outlet_id || ''}
                      onChange={(e) => handleUpdate(staffMember.user_id, { 
                        home_outlet_id: e.target.value ? parseInt(e.target.value) : null 
                      })}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                    >
                      <option value="">None</option>
                      {outlets.map((outlet) => (
                        <option key={outlet.id} value={outlet.id}>
                          {getOutletName(outlet)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="text-gray-900">
                      {staffMember.home_outlet ? getOutletName(staffMember.home_outlet) : '-'}
                    </div>
                  )}
                </div>

                <div>
                  <div className="text-xs text-gray-500 mb-1">Secondary Outlet</div>
                  {editingUserId === staffMember.user_id ? (
                    <select
                      defaultValue={staffMember.profile.secondary_outlet_id || ''}
                      onChange={(e) => handleUpdate(staffMember.user_id, { 
                        secondary_outlet_id: e.target.value ? parseInt(e.target.value) : null 
                      })}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                    >
                      <option value="">None</option>
                      {outlets.map((outlet) => (
                        <option key={outlet.id} value={outlet.id}>
                          {getOutletName(outlet)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="text-gray-700">
                      {staffMember.secondary_outlet ? getOutletName(staffMember.secondary_outlet) : '-'}
                    </div>
                  )}
                </div>

                <div>
                  <div className="text-xs text-gray-500 mb-1">Leaderboard Points</div>
                  <div className="text-gray-900">
                    <span className="font-semibold">{staffMember.skill_score}</span> pts
                    {staffMember.skill_score >= 100 && (
                      <span className="ml-1 text-xs text-purple-600">⭐</span>
                    )}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 mb-1">Type</div>
                  <button
                    onClick={() => handleUpdate(staffMember.user_id, { 
                      is_fixed: !staffMember.profile.is_fixed 
                    })}
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition ${
                      staffMember.profile.is_fixed
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {staffMember.profile.is_fixed ? 'Fixed' : 'Rotating'}
                  </button>
                </div>
              </div>

              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => handleUpdate(staffMember.user_id, { 
                    can_rotate: !staffMember.profile.can_rotate 
                  })}
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition ${
                    staffMember.profile.can_rotate
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  Can Rotate: {staffMember.profile.can_rotate ? 'Yes' : 'No'}
                </button>
                <button
                  onClick={() => handleUpdate(staffMember.user_id, { 
                    is_active: !staffMember.profile.is_active 
                  })}
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition ${
                    staffMember.profile.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {staffMember.profile.is_active ? '✓ Active' : '✕ Inactive'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {staff.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No staff profiles found.</p>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="mt-4 sm:mt-6 bg-gray-50 rounded-lg p-4 sm:p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Legend</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
          <div>
            <strong>Fixed Staff:</strong> Permanently assigned to their home outlet unless on leave
          </div>
          <div>
            <strong>Rotating Staff:</strong> Can be assigned to any outlet daily
          </div>
          <div>
            <strong>Leaderboard Points:</strong> Points from sales performance (100+ = high performer)
          </div>
          <div>
            <strong>Can Rotate:</strong> Whether staff can be assigned to outlets other than home
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffProfilesPage;
